/**
 * iter-379 — Extract vendor-brand candidates from a scope item's
 * 6-section annexure description.
 *
 * The AI emits Indian-brand equivalents as slash-separated **bold**
 * groups inside the Material Specification and Scope of Work sections,
 * e.g.
 *
 *   Supply and install a **Havells / Legrand / Schneider Electric** MCB DB…
 *   using **Polycab / Finolex / RR Kabel** copper cabling
 *
 * We scan the description for every `**A / B / C**` group whose parts
 * look like brand names (first-letter capitals, no spaces around the
 * slash), split on `/`, dedupe case-insensitively, keep insertion order.
 * A single **Bold Name** (no slashes) is only picked up when it appears
 * on a bullet that starts with a brand keyword ("Brand", "Make",
 * "Manufacturer") — otherwise every bold word would be a "brand".
 *
 * Returns a deduped array of brand strings, capped at 12 to keep the UI
 * tidy.
 */

const BRAND_CUE_RE = /^(?:brand|make|manufacturer|supplier)s?\s*[:\-]/i;
const SLASH_GROUP_RE = /\*\*([^*]+?)\*\*/g;

// Words we NEVER want to treat as a brand even if they appear bold.
// Keeps IS codes, units, dimensions and generic material words out of
// the picker.
const BRAND_BLOCKLIST = new Set([
  'is', 'iso', 'nbc', 'ecbc', 'ishrae', 'tia', 'en', 'bwr', 'bwp', 'hpl',
  'mdf', 'hdf', 'hdmr', 'mm', 'nos', 'sqft', 'sqm', 'rft', 'rmt', 'kg',
  'gsm', 'watt', 'ip', 'led', 'ac', 'dc', 'ip65', 'ip66', 'ip20', 'ip44',
  'cri', 'cct', 'k', 'v', 'w', 'a', 'ma', 'hz', 'tr', 'kw', 'kwh',
  // iter-379 fix: certifications / standards / generic terms that aren't brands.
  'bis', 'isi', 'isi mark', 'bis/isi', 'bis/isi mark',
  'installation', 'scope', 'material', 'inclusions', 'exclusions',
  'assumptions', 'labour', 'work', 'works',
]);

function looksLikeBrand(s) {
  if (!s) return false;
  const t = s.trim();
  if (t.length < 2 || t.length > 40) return false;
  if (BRAND_BLOCKLIST.has(t.toLowerCase())) return false;
  // Reject pure numbers, IS codes, dimensions.
  if (/^\d/.test(t)) return false;
  if (/^is\s*\d/i.test(t)) return false;
  // iter-379 fix: brand names never contain digits (rejects
  // "Schneider Electric 8-way MCB DB", "1mm laminate", etc.).
  if (/\d/.test(t)) return false;
  // iter-379 fix: brand names never span more than 3 words — cuts out
  // descriptor tails like "Fevicol SH / Fevicol Marine (IS 848)".
  if (t.split(/\s+/).length > 3) return false;
  // Must contain at least one letter and start with an uppercase.
  if (!/^[A-Z]/.test(t)) return false;
  if (!/[A-Za-z]/.test(t)) return false;
  return true;
}

export function extractBrandCandidates(description) {
  if (!description || typeof description !== 'string') return [];
  const brands = [];
  const seen = new Set();

  const pushBrand = (raw) => {
    // Strip trailing punctuation, unmatched brackets, IS/BIS suffixes.
    let clean = String(raw || '').trim();
    // Remove any trailing "(IS ...)", "(BIS...)", etc.
    clean = clean.replace(/\s*\([^)]*\)\s*$/, '');
    // Remove hanging "(" or ")" fragments (from partial-word truncation).
    clean = clean.replace(/[.,;:()\[\]]+$/, '').trim();
    if (!clean) return;
    const key = clean.toLowerCase();
    if (seen.has(key)) return;
    if (!looksLikeBrand(clean)) return;
    seen.add(key);
    brands.push(clean);
  };

  // Pass 1 — slash-separated bold groups.
  const boldMatches = [...description.matchAll(SLASH_GROUP_RE)];
  for (const m of boldMatches) {
    const inside = (m[1] || '').trim();
    if (!inside.includes('/')) continue;
    // iter-379 fix: skip section-heading bold groups such as
    // "**Installation / Labour Details:**" — they end with ":".
    if (inside.endsWith(':')) continue;
    // Skip bold groups that mix a brand list with a long descriptor
    // suffix like "**A / B / C 8-way MCB DB**" — split on `/`, but if
    // any part looks like a long descriptor we still try to filter via
    // looksLikeBrand. Additionally, if the whole group has a colon,
    // treat as heading and skip.
    if (inside.includes(':')) continue;
    inside.split('/').forEach((rawPart) => {
      // iter-379 fix: strip parenthetical annotations like "(IS 848)"
      // BEFORE taking word slices so we never keep an unbalanced "(IS".
      const part = rawPart.replace(/\([^)]*\)/g, '').replace(/[()]/g, '').trim();
      if (!part) return;
      const words = part.split(/\s+/);
      // Try full part first, then progressively shorter (drops trailing
      // descriptor words) — keeps "Schneider Electric" from
      // "Schneider Electric 8-way MCB DB".
      for (let len = Math.min(words.length, 3); len >= 1; len -= 1) {
        const candidate = words.slice(0, len).join(' ');
        if (looksLikeBrand(candidate)) { pushBrand(candidate); return; }
      }
    });
  }

  // Pass 2 — explicit "Brand: X" / "Make: X, Y, Z" bullets.
  const lines = description.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.replace(/^\s*[-*•]\s+/, '').trim();
    const m = line.match(BRAND_CUE_RE);
    if (!m) continue;
    const rest = line.slice(m[0].length).replace(/\*\*/g, '');
    rest.split(/[,/]/).forEach((part) => pushBrand(part));
  }

  return brands.slice(0, 12);
}
